import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Lock, ArrowLeft } from "lucide-react";
import MeditationSceneBackground from "../components/MeditationSceneBackground";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable portal password gate.
 * Props:
 *   portal: "ana" | "investor"
 */
const CONFIG = {
  ana: {
    eyebrow: "NURSES PORTAL",
    heading: "Nurses Portal",
    supporting: "Enter the access code provided for this private review.",
    footer: "Prepared for invited nurse reviewers.",
    endpoint: "/access/ana",
    sessionKey: "mmg_ana_access",
    destination: "/ana",
    confirmation: "Access confirmed. Opening the Nurses portal.",
    accent: "teal",
    testid: "ana-gate",
  },
  investor: {
    eyebrow: "PRIVATE INVESTOR PREVIEW",
    heading: "Investor Preview",
    supporting: "Enter the access code provided for this private presentation.",
    footer: "Confidential materials for invited investors and strategic partners.",
    endpoint: "/access/investor",
    sessionKey: "mmg_investor_access",
    destination: "/investor",
    confirmation: "Access confirmed. Opening the private investor presentation.",
    accent: "amber",
    testid: "investor-gate",
  },
  "living-with-cancer": {
    eyebrow: "PRIVATE CONCEPT PREVIEW",
    heading: "Living With and Beyond Cancer",
    supporting: "Enter the access code provided for this private concept review.",
    footer: "An independent concept for people living with and beyond cancer.",
    endpoint: "/access/living-with-cancer",
    sessionKey: "mmg_lwc_access",
    destination: "/living-with-cancer",
    confirmation: "Access confirmed. Opening the concept preview.",
    accent: "parasol",
    testid: "lwc-gate",
  },
};

export default function PortalGate({ portal }) {
  const cfg = CONFIG[portal];
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}${cfg.endpoint}`, { password });
      if (res.data?.success) {
        sessionStorage.setItem(cfg.sessionKey, "granted");
        setConfirmation(cfg.confirmation);
        // Small delay so the user perceives the confirmation before the redirect.
        setTimeout(() => navigate(cfg.destination), 750);
      } else {
        setError("That access code was not recognized. Please check the code provided with your invitation.");
      }
    } catch (err) {
      setError("That access code was not recognized. Please check the code provided with your invitation.");
    } finally {
      setLoading(false);
    }
  };

  const accentBorder = cfg.accent === "teal"
    ? "focus:border-teal-300/50 focus:shadow-[0_0_24px_rgba(45,212,191,0.15)] border-teal-300/15"
    : cfg.accent === "parasol"
    ? "focus:border-[#EED89A]/70 focus:shadow-[0_0_24px_rgba(238,216,154,0.20)] border-[#EED89A]/25"
    : "focus:border-amber-300/50 focus:shadow-[0_0_24px_rgba(251,191,36,0.15)] border-amber-300/20";

  const accentEyebrow = cfg.accent === "teal"
    ? "text-teal-300"
    : cfg.accent === "parasol"
    ? "text-[#EED89A]"
    : "text-amber-300";

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6 py-12 overflow-hidden"
      data-testid={`${cfg.testid}-page`}
    >
      <MeditationSceneBackground />

      <div className="relative z-10 w-full max-w-xl flex flex-col items-center text-center">
        <Link
          to="/"
          className="self-start inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.24em] mb-8 transition"
          data-testid={`${cfg.testid}-back-to-choice`}
        >
          <ArrowLeft size={13} /> Back to portal selection
        </Link>

        <div className="mb-3">
          <Logo size="xl" />
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-12 w-full max-w-md rounded-2xl p-7 sm:p-8 shadow-[0_18px_60px_rgba(0,0,0,0.35)] border border-white/10 backdrop-blur-[6px] bg-white/[0.015]"
          data-testid={`${cfg.testid}-form`}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock size={14} className={accentEyebrow} />
            <span className={`font-body text-[11px] uppercase tracking-[0.28em] ${accentEyebrow}`}>
              {cfg.eyebrow}
            </span>
          </div>

          <h1
            className="font-heading font-medium text-2xl sm:text-3xl text-white tracking-tight leading-tight mb-3"
            data-testid={`${cfg.testid}-heading`}
          >
            {cfg.heading}
          </h1>
          <p className="font-body text-sm text-zinc-400 leading-relaxed mb-6">{cfg.supporting}</p>

          <label htmlFor="password" className="sr-only">Access code</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError("");
            }}
            placeholder="Enter access code"
            autoFocus
            autoComplete="current-password"
            className={`w-full bg-white/[0.03] border ${accentBorder} focus:bg-white/[0.05] outline-none rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 font-body text-sm transition`}
            data-testid={`${cfg.testid}-password-input`}
          />

          {error ? (
            <p
              className="mt-3 text-left text-sm text-rose-300/90 font-body"
              data-testid={`${cfg.testid}-error-message`}
            >
              {error}
            </p>
          ) : null}

          {confirmation ? (
            <p
              className="mt-3 text-left text-sm text-emerald-300/90 font-body"
              data-testid={`${cfg.testid}-confirmation-message`}
            >
              {confirmation}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={loading || !password || Boolean(confirmation)}
            className="btn-premium mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 bg-white text-black font-heading font-medium tracking-tight disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`${cfg.testid}-submit-button`}
          >
            <span>{loading ? "Verifying…" : confirmation ? "Opening…" : "Continue"}</span>
            <ArrowRight size={16} />
          </button>

          <p className="mt-5 text-center text-[11px] text-zinc-500 font-body tracking-wide">
            {cfg.footer}
          </p>
        </form>
      </div>
    </div>
  );
}
